import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchWithcookieData, selectWithcookieData } from '../redux/WithcookieSlice';
import styled from 'styled-components';
import axios from 'axios';
import { useTranslation } from '../redux/TranslationHelper';

const BannerContainer = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
  aspect-ratio: 16 / 7;  
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    aspect-ratio: auto;
    height: ${props => props.isMobile ? '100vh' : 'auto'};
  }
`;

const BannerImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  position: absolute;
  top: 0;
  left: 0;
  cursor: ${props => props.hasLink ? 'pointer' : 'default'};
`;

const BannerVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  position: absolute;
  top: 0;
  left: 0;
  cursor: default;
`;

const SlideContent = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  background-size: cover;
  background-position: center;
  cursor: ${props => props.hasLink ? 'pointer' : 'default'};
  
  @media (max-width: 768px) {
    height: 100vh;
  }
`;

const BannerContent = styled.div`
  position: absolute;
  
  ${props => {
    // 9개 위치 설정 (1~9)
    // 1: 좌측 상단, 2: 가운데 상단, 3: 우측 상단
    // 4: 좌측 중앙, 5: 가운데 중앙, 6: 우측 중앙
    // 7: 좌측 하단, 8: 가운데 하단, 9: 우측 하단
    switch(props.position) {
      case '1': // 좌측 상단
        return `
          top: 40px;
          left: 40px;
          text-align: left;
          align-items: flex-start;
        `;
      case '2': // 가운데 상단
        return `
          top: 40px;
          left: 50%;
          transform: translateX(-50%);
          text-align: center;
          align-items: center;
        `;
      case '3': // 우측 상단
        return `
          top: 40px;
          right: 40px;
          text-align: right;
          align-items: flex-end;
        `;
      case '4': // 좌측 중앙
        return `
          top: 50%;
          left: 40px;
          transform: translateY(-50%);
          text-align: left;
          align-items: flex-start;
        `;
      case '5': // 가운데 중앙
        return `
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          text-align: center;
          align-items: center;
        `;
      case '6': // 우측 중앙
        return `
          top: 50%;
          right: 40px;
          transform: translateY(-50%);
          text-align: right;
          align-items: flex-end;
        `;
      case '7': // 좌측 하단
        return `
          bottom: 40px;
          left: 40px;
          text-align: left;
          align-items: flex-start;
        `;
      case '8': // 가운데 하단
        return `
          bottom: 40px;
          left: 50%;
          transform: translateX(-50%);
          text-align: center;
          align-items: center;
        `;
      case '9': // 우측 하단
        return `
          bottom: 40px;
          right: 40px;
          text-align: right;
          align-items: flex-end;
        `;
      default: // 기본값 (5: 가운데 중앙)
        return `
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          text-align: center;
          align-items: center;
        `;
    }
  }}
  
  width: ${props => props.mobileFullWidth ? '100%' : 'auto'};
  padding: 24px 30px;
  color: ${props => props.textColor || 'white'} !important;
  text-shadow: ${props => props.textShadow || '0 1px 2px rgba(0, 0, 0, 0.6)'};
  z-index: 3;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background-color: ${props => props.hasBackground ? 'rgba(0, 0, 0, 0.4)' : 'transparent'};
  backdrop-filter: ${props => props.hasBackground ? 'blur(4px)' : 'none'};
  max-width: 80%;
  
  @media (max-width: 768px) {
    padding: 20px;
    width: ${props => props.mobileFullWidth ? '100%' : 'auto'};
    max-width: 90%;
    
    // 모바일에서 전체 너비 사용 시 위치 조정
    ${props => props.mobileFullWidth && `
      left: 0 !important;
      right: auto !important;
      transform: ${props.position === '2' || props.position === '5' || props.position === '8' 
        ? 'translateY(-50%)' 
        : 'none'} !important;
    `}
  }
`;

const BannerTitle = styled.h2`
  font-size: 48px;
  margin-top: 24px !important;
  margin-bottom: 24px !important;
  font-weight: bold;
  letter-spacing: -0.5px;
  color: inherit;
  
  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 12px;
  }
`;

const BannerDescription = styled.p`
  font-size: 20px;
  margin-bottom: 0px !important;
  line-height: 1.5;
  color: inherit;
  
  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 16px;
  }
`;

const CTAButton = styled.button`
  background-color: ${props => props.transparent ? 'transparent' : (props.buttonBgColor || '#ff6b6b')};
  color: ${props => props.buttonTextColor || 'white'};
  border: ${props => props.transparent ? `1px solid ${props.buttonTextColor || 'white'}` : 'none'};
  padding: 12px 24px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
  border-radius: 4px;
  font-size: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${props => props.transparent ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.1)'};
  width: ${props => props.width || 'auto'};
  height: ${props => props.height || 'auto'};
  
  &:hover {
    background-color: ${props => props.transparent ? 'rgba(255, 255, 255, 0.1)' : (props.buttonHoverColor || '#ff5252')};
    transform: translateY(-2px);
    box-shadow: ${props => props.transparent ? 'none' : '0 4px 8px rgba(0, 0, 0, 0.15)'};
  }
  
  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 14px;
    width: ${props => props.mobileWidth || props.width || 'auto'};
    height: ${props => props.mobileHeight || props.height || 'auto'};
  }
  
  /* 화살표 아이콘 추가 */
  
  
  &:hover::after {
    transform: translateX(4px);
  }
`;

const LoadingIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  aspect-ratio: 16 / 7;
  background-color: #f5f5f5;
  color: #666;
  font-size: 18px;
`;

// 간단한 슬라이더 구현
const SliderContainer = styled.div`
  width: 100%;
  aspect-ratio: 16 / 7;
  position: relative;
  overflow: hidden;
`;

const SliderWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const Slide = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: ${props => props.active ? 1 : 0};
  visibility: ${props => props.active ? 'visible' : 'hidden'};
  transition: opacity 0.8s ease, visibility 0.8s ease, transform 0.3s ease;
  z-index: ${props => props.active ? 2 : 1};
  
  &:hover {
    transform: ${props => props.active ? 'scale(1.01)' : 'none'};
  }
`;

// 배너 네비게이션 버튼
const NavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.3);
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 10;
  font-size: 20px;
  
  &:hover {
    background: rgba(0, 0, 0, 0.5);
  }
  
  &.prev {
    left: 10px;
  }
  
  &.next {
    right: 10px;
  }
`;

// 배너 인디케이터
const Indicators = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 10;
`;

const Indicator = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.5)'};
  cursor: pointer;
`;

// 리스트 모드 배너 컨테이너
const BannerListContainer = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: nowrap;
  gap: 16px;
  overflow-x: auto;
  padding: 16px 0;
  scrollbar-width: thin;
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

// 리스트 모드 배너 아이템
const BannerListItem = styled.div`
  flex: 0 0 auto;
  width: 300px;
  aspect-ratio: 16 / 9;
  position: relative;
  border-radius: 0;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px) scale(1.02);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
  }
`;

const MainBannerComponent = ({ mode = 'slider', style, componentProps = {} }) => {
  const dispatch = useDispatch();
  const withcookieData = useSelector(selectWithcookieData);
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const initialDataLoaded = useRef(false);
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const videoRef = useRef(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const { t } = useTranslation();
  
  // 이미지 업로드 핸들러
  const handleImageUpload = async (file) => {
    if (!file) return null;
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bucket', 'withcookie-bucket');
      formData.append('path', 'c/default');

      const res = await axios.post(
        `https://refrigerator.logipasta.com/v1/file`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Access-Control-Allow-Origin': '*',
          },
        }
      );

      if (res.data && res.data.file) {
        return res.data.file;
      }
      return null;
    } catch (err) {
      console.error(t('[배너] 이미지 업로드 실패:'), err);
      return null;
    }
  };

  useEffect(() => {
    // withcookieData가 없는 경우 데이터를 가져옵니다
    if (!withcookieData) {
      dispatch(fetchWithcookieData())
        .catch(error => {
          console.error(t('배너 데이터 로딩 실패:'), error);
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, [dispatch, withcookieData, t]);

  // 이미지 소스 가져오기
  const getImageSource = (banner, index) => {
    if (!banner.icon) {
      return 'https://via.placeholder.com/1200x400';
    }
    
    // S3 URL 패턴 확인
    if (typeof banner.icon === 'string' && banner.icon.includes('amazonaws.com')) {
      return banner.icon;
    }
    
    // 상대 경로인 경우 기본 URL 추가
    if (typeof banner.icon === 'string' && banner.icon.startsWith('/')) {
      return `${window.location.origin}${banner.icon}`;
    }
    
    return banner.icon;
  };
  
  // componentProps에서 배너 데이터 읽기
  useEffect(() => {
    try {
      // 다양한 데이터 구조 처리를 위한 배너 데이터 찾기
      let bannerData = null;
      
      // 0. deviceProperty 객체 내의 배너 확인 (최우선)
      if (componentProps?.deviceProperty) {
        const deviceType = isMobile ? 'mobile' : 'pc';
        if (componentProps.deviceProperty[deviceType]?.banners && 
            Array.isArray(componentProps.deviceProperty[deviceType].banners)) {
          bannerData = JSON.parse(JSON.stringify(componentProps.deviceProperty[deviceType].banners));
          
          // 직접 전달된 데이터 처리
          const processedData = bannerData.map(banner => processInitialBanner(banner, componentProps));
          
          // 항상 새로운 배너 데이터로 업데이트
          setBanners(processedData);
          initialDataLoaded.current = true;
          setIsLoading(false);
          return;
        }
      }
      
      // 1. 직접 전달된 banners 배열
      if (componentProps?.banners && Array.isArray(componentProps.banners)) {
        bannerData = JSON.parse(JSON.stringify(componentProps.banners)); // 깊은 복사
        
        // 직접 전달된 데이터 처리
        const processedData = bannerData.map(banner => processInitialBanner(banner, componentProps));
        
        // 항상 새로운 배너 데이터로 업데이트
        setBanners(processedData);
        initialDataLoaded.current = true;
        setIsLoading(false);
        return;
      } 
      // 2. data 객체 내의 banners 배열
      else if (componentProps?.data?.banners && Array.isArray(componentProps.data.banners)) {
        bannerData = JSON.parse(JSON.stringify(componentProps.data.banners)); // 깊은 복사
      }
      // 3. content 객체 내의 banners 배열
      else if (componentProps?.content?.banners && Array.isArray(componentProps.content.banners)) {
        bannerData = JSON.parse(JSON.stringify(componentProps.content.banners)); // 깊은 복사
      }
      
      // 배너 데이터가 발견된 경우 적용
      if (bannerData && bannerData.length > 0) {
        
        // 데이터 처리
        const processedData = bannerData.map(banner => processInitialBanner(banner, componentProps));
        
        setBanners(processedData);
        initialDataLoaded.current = true;
        setIsLoading(false);
        return;
      }
      
      // 하드코딩된 배너 데이터 제거하고 빈 배열 설정
      setBanners([]);
      initialDataLoaded.current = true;
      setIsLoading(false);
      
    } catch (error) {
      console.error(t('[배너] 데이터 처리 중 오류:'), error);
      // 오류 발생 시 빈 배열 설정
      setBanners([]);
      initialDataLoaded.current = true;
      setIsLoading(false);
    }
  }, [componentProps, isMobile, t]); // withcookieData와 componentProps, isMobile이 변경될 때마다 실행
  
  // 배너 데이터에 속성 설정 및 처리
  const processInitialBanner = (banner, props) => {
    
    // 새 배너가 추가될 때 공통 속성을 상속받지 않도록 수정
    // 기존 배너 데이터 자체는 유지하고 누락된 속성만 기본값으로 설정
    return {
      ...banner,
      // 각 배너는 자체 속성을 가지며, 속성이 없는 경우에만 기본값 사용
      showTitle: banner.showTitle !== undefined ? banner.showTitle : true,
      showButton: banner.showButton !== undefined ? banner.showButton : true,
      
      // 기존 위치 속성을 사용하여 position 속성 설정
      position: banner.position || '5', // 기본값: 5 (가운데 중앙)
      
      // 하위 호환성을 위해 기존 horizontalPosition, verticalPosition 속성을 기반으로 position 결정
      ...((!banner.position && (banner.horizontalPosition || banner.verticalPosition)) && {
        position: getPositionFromLegacy(banner.horizontalPosition, banner.verticalPosition)
      }),
      
      textColor: banner.textColor || 'white',
      textShadow: banner.textShadow || '0 1px 2px rgba(0, 0, 0, 0.6)',
      buttonBgColor: banner.buttonBgColor || '#ff6b6b',
      buttonTextColor: banner.buttonTextColor || 'white',
      buttonHoverColor: banner.buttonHoverColor || '#ff5252',
      buttonText: banner.buttonText || t('자세히 보기'),
      // 새로 추가된 버튼 속성
      transparentButton: banner.transparentButton !== undefined ? banner.transparentButton : false,
      buttonWidth: banner.buttonWidth || '300px',
      buttonHeight: banner.buttonHeight || 'auto',
      mobileButtonWidth: banner.mobileButtonWidth || 'auto',
      mobileButtonHeight: banner.mobileButtonHeight || 'auto',
      // 기존 속성 계속
      mobileFullWidth: banner.mobileFullWidth || false,
      url: banner.url || '#',
      text: banner.text || '',
      description: banner.description || '',
      videoUrl: banner.videoUrl || '',
      mediaType: banner.mediaType || (banner.videoUrl ? 'video' : 'image'),
      autoplay: true, // 항상 자동 재생 설정
      muted: true, // 항상 음소거 설정 (자동재생을 위해 필요)
      loop: banner.loop !== undefined ? banner.loop : true,
      hasBackground: banner.hasBackground !== undefined ? banner.hasBackground : false
    };
  };
  
  // 레거시 위치 속성에서 새로운 position 값을 결정하는 함수
  const getPositionFromLegacy = (horizontal = 'center', vertical = 'center') => {
    const hPos = horizontal.toLowerCase();
    const vPos = vertical.toLowerCase();
    
    if (vPos === 'top') {
      if (hPos === 'left') return '1';
      if (hPos === 'center') return '2';
      if (hPos === 'right') return '3';
    }
    else if (vPos === 'center' || vPos === 'middle') {
      if (hPos === 'left') return '4';
      if (hPos === 'center') return '5';
      if (hPos === 'right') return '6';
    }
    else if (vPos === 'bottom') {
      if (hPos === 'left') return '7';
      if (hPos === 'center') return '8';
      if (hPos === 'right') return '9';
    }
    
    return '5'; // 기본값: 가운데 중앙
  };
  
  // props 변경 감지 및 적용
  useEffect(() => {
    if (!componentProps) return;
    
    let needsUpdate = false;
    let updatedBanners = [...banners];
    
    // 배너 추가 감지
    if (componentProps.addBanner) {
      needsUpdate = true;
      
      // 새 배너 추가 시 기본값으로 설정하되, 제공된 속성은 유지
      const newBanner = {
        // 기본값 설정
        showTitle: true,
        showButton: true,
        position: '5',
        textColor: 'white',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.6)',
        buttonBgColor: '#ff6b6b',
        buttonTextColor: 'white',
        buttonHoverColor: '#ff5252',
        buttonText: t('자세히 보기'),
        // 새로 추가된 버튼 속성
        transparentButton: false,
        buttonWidth: '300px',
        buttonHeight: 'auto',
        mobileButtonWidth: 'auto',
        mobileButtonHeight: 'auto',
        // 기존 속성 계속
        mobileFullWidth: false,
        url: '#',
        text: '',
        description: '',
        // 제공된 속성으로 덮어쓰기
        ...componentProps.addBanner
      };
      
      updatedBanners = [...updatedBanners, newBanner];
    }
    
    // 배너 속성 업데이트 감지
    if (componentProps.updateBanner && componentProps.updateIndex !== undefined) {
      const updateIndex = componentProps.updateIndex;
      
      if (updateIndex >= 0 && updateIndex < updatedBanners.length) {
        needsUpdate = true;
        const targetBanner = {...updatedBanners[updateIndex]};
        
        // 업데이트할 속성만 적용
        Object.keys(componentProps.updateBanner).forEach(key => {
          targetBanner[key] = componentProps.updateBanner[key];
        });
        
        updatedBanners[updateIndex] = targetBanner;
      }
    }
    
    // 레거시: 업데이트된 스타일 또는 설정이 있는 경우, 이를 현재 배너에 적용
    if (componentProps.updateStyle && !componentProps.addBanner) {
      
      const updateIndex = componentProps.updateIndex !== undefined ? componentProps.updateIndex : 0;
      
      if (updateIndex >= 0 && updateIndex < updatedBanners.length) {
        needsUpdate = true;
        const targetBanner = {...updatedBanners[updateIndex]};
        
        // 중요: 개별 속성 업데이트 (깊은 병합)
        Object.keys(componentProps.updateStyle).forEach(key => {
          targetBanner[key] = componentProps.updateStyle[key];
        });
        
        updatedBanners[updateIndex] = targetBanner;
      }
    }
    
    // 직접 전체 배너 데이터가 업데이트된 경우
    if (componentProps.updatedBanners && Array.isArray(componentProps.updatedBanners)) {
      needsUpdate = true;
      // 전체 배너를 변경할 때는 각 배너 데이터를 그대로 유지
      updatedBanners = componentProps.updatedBanners;
    }
    
    // 필요한 경우에만 상태 업데이트
    if (needsUpdate) {
      setBanners(updatedBanners);
    }
  }, [componentProps]); // banners 의존성 제거로 무한 루프 방지
  
  // 배너 스타일 문자열을 객체로 변환하는 함수
  const parseStyleString = (styleStr) => {
    if (!styleStr) return {};
    
    const styleObj = {};
    const styles = styleStr.split(';');
    
    styles.forEach(style => {
      const [property, value] = style.split(':').map(item => item.trim());
      if (property && value) {
        // 카멜 케이스로 변환 (예: background-color -> backgroundColor)
        const camelProperty = property.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
        styleObj[camelProperty] = value;
      }
    });
    
    return styleObj;
  };

  // 다음 배너로 이동
  const nextBanner = useCallback(() => {
    setCurrentIndex((prevIndex) => 
      prevIndex === banners.length - 1 ? 0 : prevIndex + 1
    );
  }, [banners.length]);

  // 이전 배너로 이동
  const prevBanner = useCallback(() => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? banners.length - 1 : prevIndex - 1
    );
  }, [banners.length]);

  // 특정 배너로 이동
  const goToBanner = useCallback((index) => {
    setCurrentIndex(index);
  }, []);

  // 자동 슬라이드 효과 - 비디오가 아닌 경우에만 타이머로 작동
  useEffect(() => {
    if (banners.length <= 1 || mode === 'list') return;
    
    // 현재 배너가 비디오인지 확인
    const currentBanner = banners[currentIndex];
    const isCurrentBannerVideo = currentBanner?.mediaType === 'video' && currentBanner?.videoUrl;
    
    // 비디오가 아닌 경우에만 타이머 설정
    let interval;
    if (!isCurrentBannerVideo) {
      interval = setInterval(() => {
        nextBanner();
      }, 5000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [banners.length, mode, nextBanner, currentIndex, banners]);

  // 비디오 끝났을 때 다음 배너로 이동하는 핸들러
  const handleVideoEnded = useCallback(() => {
    setIsVideoPlaying(false);
    // 비디오가 끝나면 다음 배너로 이동
    nextBanner();
  }, [nextBanner, t]);

  // 비디오 재생 시작 핸들러
  const handleVideoPlaying = useCallback(() => {
    setIsVideoPlaying(true);
  }, [t]);

  // 비디오 로드/에러 핸들러
  const handleVideoLoaded = useCallback(() => {
    
  }, []);

  const handleVideoError = useCallback((e) => {
    console.error(t('비디오 로드 오류:'), e.target.error);
  }, [t]);

  // 비디오 자동재생 강제 적용
  useEffect(() => {
    if (banners.length === 0) return;
    
    // 현재 배너가 비디오인지 확인
    const currentBanner = banners[currentIndex];
    const isVideo = currentBanner?.mediaType === 'video' && currentBanner?.videoUrl;
    
    // 비디오가 있고 videoRef가 존재하면 재생 시도
    if (isVideo && videoRef.current) {
      
      // 자동재생을 위한 함수
      const playVideo = async () => {
        try {
          // 자동재생 보장을 위한 설정
          videoRef.current.muted = true;
          
          const playPromise = videoRef.current.play();
          
          if (playPromise !== undefined) {
            playPromise.then(() => {
              
            }).catch(error => {
              
              
              // 경우에 따라 사용자 상호작용 없이 자동재생이 차단될 수 있음
              // 이런 경우 브라우저 정책으로 인해 발생하며 음소거 상태여도 발생할 수 있음
            });
          }
        } catch (error) {
          console.error(t('비디오 재생 중 오류 발생:'), error);
        }
      };
      
      // 비디오 재생 시도
      playVideo();
    }
  }, [currentIndex, banners, t]);

  if (isLoading) {
    return <div style={{padding: '20px', textAlign: 'center'}}>{t('배너 로딩 중...')}</div>;
  }

  // 배너가 없는 경우
  if (!banners || banners.length === 0) {
    return (
      <BannerContainer style={{...style, paddingLeft: undefined, paddingRight: undefined}} isMobile={isMobile}>
        {isMobile ? (
          <SlideContent 
            style={{ 
              backgroundColor: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        ) : (
          <div style={{
            width: '100%',
            height: '100%',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}></div>
        )}
        <BannerContent 
          position="5"
          textColor="#333333"
          textShadow="none"
        >
          <BannerTitle>{t('배너가 없습니다')}</BannerTitle>
          <BannerDescription>{t('배너를 추가해주세요')}</BannerDescription>
        </BannerContent>
      </BannerContainer>
    );
  }

  // 배너가 하나뿐인 경우 단일 배너 표시
  if (banners.length === 1) {
    const banner = banners[0];
    const customStyle = parseStyleString(banner.style);
    const isVideo = banner.mediaType === 'video' && banner.videoUrl;
    
    // 이미지 소스 가져오기
    const imgSrc = getImageSource(banner, 0);
    
    
    return (
      <BannerContainer style={{
        ...style, 
        ...customStyle, 
        paddingLeft: undefined, 
        paddingRight: undefined,
        height: isMobile ? '100vh' : undefined
      }} isMobile={isMobile}>
        {isMobile ? (
          isVideo ? (
            <BannerVideo
              ref={videoRef}
              src={banner.videoUrl}
              autoPlay
              playsInline
              muted
              loop={banner.loop}
              preload="auto"
              disablePictureInPicture
              controlsList="nodownload nofullscreen noremoteplayback"
              onError={handleVideoError}
              onLoadedData={handleVideoLoaded}
              onPlay={handleVideoPlaying}
              onEnded={() => !banner.loop && handleVideoEnded()}
            />
          ) : (
            <SlideContent 
              style={{ 
                backgroundImage: `url(${imgSrc})`,
                cursor: 'default'
              }}
            />
          )
        ) : (
          isVideo ? (
            <BannerVideo
              ref={videoRef}
              src={banner.videoUrl}
              autoPlay
              playsInline
              muted
              loop={banner.loop}
              preload="auto"
              disablePictureInPicture
              controlsList="nodownload nofullscreen noremoteplayback"
              onError={handleVideoError}
              onLoadedData={handleVideoLoaded}
              onPlay={handleVideoPlaying}
              onEnded={() => !banner.loop && handleVideoEnded()}
            />
          ) : (
            <BannerImage 
              src={imgSrc}
              alt={banner.text || t('배너 이미지')} 
              style={{ cursor: 'default' }}
            />
          )
        )}
        {(banner.showTitle || banner.showButton) && (
          <BannerContent 
            position={banner.position}
            textColor={banner.textColor}
            textShadow={banner.textShadow}
            mobileFullWidth={banner.mobileFullWidth}
            hasBackground={banner.hasBackground}
          >
            {banner.showTitle && banner.description && <BannerDescription>{t(banner.description)}</BannerDescription>}
            {banner.showTitle && banner.text && <BannerTitle>{t(banner.text)}</BannerTitle>}
            
            {banner.showButton && banner.url && banner.url !== '#' && (
              <CTAButton 
                onClick={() => window.location.href = banner.url}
                buttonBgColor={banner.buttonBgColor}
                buttonTextColor={banner.buttonTextColor}
                buttonHoverColor={banner.buttonHoverColor}
                transparent={banner.transparentButton}
                width={banner.buttonWidth}
                height={banner.buttonHeight}
                mobileWidth={banner.mobileButtonWidth}
                mobileHeight={banner.mobileButtonHeight}
              >
                {t(banner.buttonText)}
              </CTAButton>
            )}
          </BannerContent>
        )}
      </BannerContainer>
    );
  }

  // 리스트 모드 (가로 스크롤)
  if (mode === 'list') {
    return (
      <BannerListContainer style={{
        ...style, 
        paddingLeft: undefined, 
        paddingRight: undefined,
        height: isMobile ? '100vh' : undefined
      }}>
        {banners.map((banner, index) => {
          const customStyle = parseStyleString(banner.style);
          const imgSrc = getImageSource(banner, index);
          const isVideo = banner.mediaType === 'video' && banner.videoUrl;
          
          return (
            <BannerListItem key={index} style={customStyle}>
              {isMobile ? (
                isVideo ? (
                  <BannerVideo
                    src={banner.videoUrl}
                    autoPlay
                    playsInline
                    muted
                    loop={banner.loop}
                    disablePictureInPicture
                    controlsList="nodownload nofullscreen noremoteplayback"
                  />
                ) : (
                  <SlideContent 
                    style={{ 
                      backgroundImage: `url(${imgSrc})`,
                      cursor: 'default'
                    }}
                  />
                )
              ) : (
                isVideo ? (
                  <BannerVideo
                    src={banner.videoUrl}
                    autoPlay
                    playsInline
                    muted
                    loop={banner.loop}
                    disablePictureInPicture
                    controlsList="nodownload nofullscreen noremoteplayback"
                  />
                ) : (
                  <BannerImage 
                    src={imgSrc}
                    alt={banner.text || `배너 이미지`} 
                    style={{ cursor: 'default' }}
                  />
                )
              )}
              {(banner.showTitle || banner.showButton) && (
                <BannerContent 
                  position={banner.position}
                  textColor={banner.textColor}
                  textShadow={banner.textShadow}
                  mobileFullWidth={banner.mobileFullWidth}
                  hasBackground={banner.hasBackground}
                >
                  {banner.showTitle && banner.description && <BannerDescription>{t(banner.description)}</BannerDescription>}
                  {banner.showTitle && banner.text && <BannerTitle>{t(banner.text)}</BannerTitle>}
                  
                  {banner.showButton && banner.url && banner.url !== '#' && (
                    <CTAButton 
                      onClick={() => window.location.href = banner.url}
                      buttonBgColor={banner.buttonBgColor}
                      buttonTextColor={banner.buttonTextColor}
                      buttonHoverColor={banner.buttonHoverColor}
                      transparent={banner.transparentButton}
                      width={banner.buttonWidth}
                      height={banner.buttonHeight}
                      mobileWidth={banner.mobileButtonWidth}
                      mobileHeight={banner.mobileButtonHeight}
                    >
                      {t(banner.buttonText)}
                    </CTAButton>
                  )}
                </BannerContent>
              )}
            </BannerListItem>
          );
        })}
      </BannerListContainer>
    );
  }

  // 표준 슬라이더 모드 - 간단한 CSS 트랜지션만으로 구현
  return (
    <SliderContainer style={{
      ...style, 
      paddingLeft: undefined, 
      paddingRight: undefined,
      height: isMobile ? '100vh' : undefined
    }}>
      <SliderWrapper>
        {banners.map((banner, index) => {
          const customStyle = parseStyleString(banner.style);
          const imgSrc = getImageSource(banner, index);
          const isVideo = banner.mediaType === 'video' && banner.videoUrl;
          
          return (
            <Slide key={index} active={index === currentIndex} style={customStyle}>
              {isMobile ? (
                isVideo ? (
                  <BannerVideo
                    ref={index === currentIndex ? videoRef : null}
                    src={banner.videoUrl}
                    autoPlay={index === currentIndex}
                    playsInline
                    muted={true}
                    loop={banner.loop}
                    preload="auto"
                    disablePictureInPicture
                    controlsList="nodownload nofullscreen noremoteplayback"
                    onError={handleVideoError}
                    onLoadedData={handleVideoLoaded}
                    onPlay={handleVideoPlaying}
                    onEnded={() => !banner.loop && index === currentIndex && handleVideoEnded()}
                  />
                ) : (
                  <SlideContent 
                    style={{ 
                      backgroundImage: `url(${imgSrc})`,
                      cursor: 'default'
                    }}
                  />
                )
              ) : (
                isVideo ? (
                  <BannerVideo
                    ref={index === currentIndex ? videoRef : null}
                    src={banner.videoUrl}
                    autoPlay={index === currentIndex}
                    playsInline
                    muted={true}
                    loop={banner.loop}
                    preload="auto"
                    disablePictureInPicture
                    controlsList="nodownload nofullscreen noremoteplayback"
                    onError={handleVideoError}
                    onLoadedData={handleVideoLoaded}
                    onPlay={handleVideoPlaying}
                    onEnded={() => !banner.loop && index === currentIndex && handleVideoEnded()}
                  />
                ) : (
                  <BannerImage 
                    src={imgSrc}
                    alt={banner.text || t('배너 이미지')} 
                    style={{ cursor: 'default' }}
                  />
                )
              )}
              {(banner.showTitle || banner.showButton) && (
                <BannerContent 
                  position={banner.position}
                  textColor={banner.textColor}
                  textShadow={banner.textShadow}
                  mobileFullWidth={banner.mobileFullWidth}
                  hasBackground={banner.hasBackground}
                >
                  {banner.showTitle && banner.description && <BannerDescription>{t(banner.description)}</BannerDescription>}
                  {banner.showTitle && banner.text && <BannerTitle>{t(banner.text)}</BannerTitle>}
                  
                  {banner.showButton && banner.url && banner.url !== '#' && (
                    <CTAButton 
                      onClick={() => window.location.href = banner.url}
                      buttonBgColor={banner.buttonBgColor}
                      buttonTextColor={banner.buttonTextColor}
                      buttonHoverColor={banner.buttonHoverColor}
                      transparent={banner.transparentButton}
                      width={banner.buttonWidth}
                      height={banner.buttonHeight}
                      mobileWidth={banner.mobileButtonWidth}
                      mobileHeight={banner.mobileButtonHeight}
                    >
                      {t(banner.buttonText)}
                    </CTAButton>
                  )}
                </BannerContent>
              )}
            </Slide>
          );
        })}
      </SliderWrapper>
      
      {/* 네비게이션 버튼 */}
      {banners.length > 1 && (
        <>
          <NavButton className="prev" onClick={prevBanner}>&#10094;</NavButton>
          <NavButton className="next" onClick={nextBanner}>&#10095;</NavButton>
          
          {/* 인디케이터 */}
          <Indicators>
            {banners.map((_, index) => (
              <Indicator 
                key={index} 
                active={index === currentIndex}
                onClick={() => goToBanner(index)}
              />
            ))}
          </Indicators>
        </>
      )}
    </SliderContainer>
  );
};

export default MainBannerComponent; 